import { useEffect, useMemo, useState } from 'react'
import { Box, Chip, Link, Stack, Typography } from '@mui/material'
import { CheckIcon, ImageIcon, SparklesIcon, XIcon } from 'lucide-react'

import { Alert, Button, Card, Input, Skeleton, TextArea } from '@repo/ui'
import { ConfirmSettingModal } from 'components'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import {
  useApproveCover,
  useCoverImageUrl,
  useCoverState,
  useGenerateConcepts,
  useGenerateSample,
  useGenerateTests,
  useRetryCoverPr,
  useSetCoverCap,
} from 'hooks/api/useArticleCovers'
import { coversApi, type CoverConcept, type CoverConceptSet, type CoverImage, type CoverRun, type CoverState } from 'services/coversApi'


/**
 * The Cover tab of `/internal/gtm/articles/:section/:slug` (`?tab=cover`) — VRG-733: generate an
 * /insights article's cover. (Its first home, `…/:slug/cover`, redirects to the tab.)
 *
 * The flow Spencer agreed (2026-10-22), one card per step:
 *   1. Concepts — 4 ideas from the article, each tied to a sentence quoted VERBATIM from it.
 *   2. Pick one, or edit it (THE IDEA paragraph, the alt), or write a custom prompt.
 *   3. 4 tests at 2K, each with its QA checks. Test 1 is as written; 2-4 each add one variation.
 *      Or (rules v2, 2026-10-23): "1 image of each concept" — one test of each of the 4 ideas, to
 *      compare them before spending 4 tests on one.
 *   4. Pick one test: THAT is the cover. It is never regenerated at 4K (a regeneration is a
 *      different picture); approve can upscale it 2x with Imagen's upscaler instead, which keeps
 *      the picture (2026-10-29, ~$0.06, on by default in live).
 *   5. Approve: records it, writes the 2400x1340 WebP, opens a PR on vera-website (mock: plans
 *      it), or with ARTICLE_COVERS_APPLY=local writes the files into the local website checkout.
 *
 * Every paid button carries its estimate ("Generate 4 tests · ~$0.57") and pressing it IS the
 * confirmation; the server re-checks the monthly cap first and refuses with the reason. Writes
 * are platform_admin only (server-enforced; here disabled with the reason, §27).
 */

const usd = (n: number | null | undefined, d = 3) => (n == null ? '—' : `$${n.toFixed(d)}`)
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
const VARIATION_LABEL: Record<string, string> = { 'as-written': 'as written', camera: 'camera +10°', light: 'tighter light', framing: 'framed smaller' }
const SCENE_LABEL: Record<string, string> = {
  'people-at-work': 'People at work',
  'process-detail': 'Process detail',
  place: 'Place',
  'overhead-arrangement': 'Overhead arrangement',
  'object-still-life': 'Object still life',
  'macro-detail': 'Macro detail',
  'vitrine-display': 'Vitrine / display',
  'environment-scene': 'Environment (v1)',
}
const ROLE_LABEL: Record<string, string> = { subject: 'object is the subject', 'in-scene': 'object in the scene', absent: 'no object' }
const TAG_LABEL: Record<string, string> = { hidden: 'tag hidden', 'in-situ': 'tag in situ', visible: 'tag visible', none: 'no tag' }

/** The Cover tab of the article page (`ArticleDetailPage`, `?tab=cover`). */
export const ArticleCoverPanel = ({ section, slug, title }: { section: string; slug: string; title: string }) => {
  const { data: state, isLoading, error } = useCoverState(section, slug, section === 'insights')
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })

  if (section !== 'insights') {
    return <Alert severity="info" title="No cover for explainers" description="Explainers have no hero image, by decision; the cover tool is for /insights articles." />
  }
  return (
    <>
      <Typography variant="body3" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
        Concepts from the article, one image of each or four tests of the one you pick, then approve. The test you pick is the cover; nothing is regenerated after it.
      </Typography>
      {error ? <Alert severity="error" title="Could not load the cover state" description={(error as Error).message} /> : null}
      {isLoading || !state ? (
        <Stack spacing={3}>
          <Skeleton height={80} />
          <Skeleton height={240} />
        </Stack>
      ) : (
        <CoverFlow state={state} section={section} slug={slug} title={title} isAdmin={isAdmin} />
      )}
    </>
  )
}

const CoverFlow = ({ state, section, slug, title, isAdmin }: { state: CoverState; section: string; slug: string; title: string; isAdmin: boolean }) => {
  const { open: notify } = useSnackbar()
  const latest = state.conceptSets[0] ?? null
  const [pick, setPick] = useState<{ setId: string; conceptId: string } | null>(null)
  useEffect(() => {
    if (latest && (!pick || pick.setId !== latest.id)) setPick(null)
    // a new concept set clears the pick
  }, [latest?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const picked = latest && pick ? latest.draft.concepts.find((c) => c.id === pick.conceptId) ?? null : null
  const writeBlocked = !isAdmin ? 'Only a Vera admin can run cover generation.' : !state.config.ready ? 'Live mode is not configured; see the banner above.' : null
  /* apply writes to the website: it can be blocked while generation runs (no GitHub token) */
  const applyBlocked = writeBlocked ?? (state.config.applyReady === false ? `Applying needs ${(state.config.applyMissing ?? []).join(', ')}.` : null)

  return (
    <Stack spacing={4}>
      <StatusCard state={state} section={section} slug={slug} isAdmin={isAdmin} />
      <ConceptsCard state={state} section={section} slug={slug} title={title} latest={latest} pickedId={pick?.conceptId ?? null} onPick={(c) => setPick(latest ? { setId: latest.id, conceptId: c.id } : null)} writeBlocked={writeBlocked} notify={notify} />
      {latest && picked ? <EditCard key={`${latest.id}:${picked.id}`} state={state} section={section} slug={slug} set={latest} concept={picked} writeBlocked={writeBlocked} notify={notify} /> : null}
      {state.tests ? <TestsCard state={state} section={section} slug={slug} title={title} writeBlocked={applyBlocked} notify={notify} /> : null}
      {state.approval ? <ApprovedCard state={state} section={section} slug={slug} title={title} isAdmin={isAdmin} notify={notify} /> : null}
      <RunLogCard runs={state.runs} />
    </Stack>
  )
}

type Notify = ReturnType<typeof useSnackbar>['open']

// ── status: mode, cap, article source ────────────────────────────────────────
const StatusCard = ({ state, section, slug, isAdmin }: { state: CoverState; section: string; slug: string; isAdmin: boolean }) => {
  const { config, cap, article, pin } = state
  const [capInput, setCapInput] = useState(String(cap.capUsd))
  const setCap = useSetCoverCap(section, slug)
  const { open: notify } = useSnackbar()
  const pct = cap.capUsd > 0 ? Math.min(100, (cap.spentUsd / cap.capUsd) * 100) : 100
  return (
    <Card sx={{ p: 4, mb: 0 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
          <Chip
            size="small"
            label={
              config.mode === 'mock'
                ? 'Mock mode'
                : !config.ready
                ? 'Live: not configured'
                : config.applyReady === false
                ? `Live: generation ready · apply needs ${(config.applyMissing ?? []).join(', ')}`
                : 'Live'
            }
            color={config.mode === 'mock' ? 'default' : config.ready ? 'success' : 'warning'}
          />
          <Typography variant="body3" color="text.secondary">
            {config.models.image} at {config.imageSize} {config.aspectRatio} · concepts {config.models.concept} · QA {config.models.qa} · Vertex {config.vertexProject ?? 'n/a'} / {config.vertexLocation}
          </Typography>
        </Stack>
        {config.mode === 'mock' ? (
          <Alert
            severity="info"
            title="Mock mode: no model is called and nothing is sent."
            description="Concepts are canned from the article's own sentences, tests are placeholder images with canned QA, and the website PR is only planned. Costs shown are what a live run would cost; mock spend counts against the cap only in mock."
          />
        ) : null}
        {!config.ready ? <Alert severity="warning" title="Live mode is on but not configured" description={`Missing: ${config.missing.join(', ')}. Every paid action is refused until these are set.`} /> : null}
        {config.mode === 'live' && config.ready && config.applyReady === false ? (
          <Alert
            severity="info"
            title="Generation is live; applying a cover is off"
            description={`Concepts, images and QA run on Vertex and are stored in ${config.store}. Article text comes from ${config.articleSource ?? 'github'}. Picking a test as the cover writes to the website, which needs ${(config.applyMissing ?? []).join(', ')}; until then it is refused. This page shows ${config.mode} rows only.`}
          />
        ) : null}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body3" sx={{ fontWeight: 600 }}>
              {usd(cap.spentUsd, 2)} of {usd(cap.capUsd, 2)} spent in {cap.month} ({cap.mode})
            </Typography>
            <Box sx={(t) => ({ mt: 1, height: 6, borderRadius: 3, backgroundColor: t.palette.primary[100], overflow: 'hidden' })}>
              <Box sx={{ width: `${pct}%`, height: '100%', backgroundColor: pct >= 100 ? 'error.main' : 'primary.main' }} />
            </Box>
            <Typography variant="body4" color="text.secondary">
              A run starts only if this month&apos;s spend plus the run&apos;s highest estimate stays within the cap.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <Box sx={{ width: 140 }}>
              <Input label="Monthly cap (USD)" value={capInput} onChange={setCapInput} disabled={!isAdmin} />
            </Box>
            <Button
              variant="secondary"
              size="small"
              disabled={!isAdmin || setCap.isPending || Number(capInput) === cap.capUsd || !Number.isFinite(Number(capInput))}
              onClick={() =>
                setCap.mutate(Number(capInput), {
                  onSuccess: (c) => notify({ type: 'success', text: `Cap set to $${c.capUsd.toFixed(2)} a month` }),
                  onError: (e) => notify({ type: 'error', text: (e as Error).message }),
                })
              }
            >
              Save cap
            </Button>
          </Stack>
        </Stack>
        <Typography variant="body4" color="text.secondary">
          Article text: {article.available ? `${article.words.toLocaleString('en-US')} words from ${article.source}` : article.reason}
          {pin ? ` · object pinned${pin.optional ? ' (optional: a concept may leave it out)' : ''}: ${pin.class} (${pin.by})` : ''} · style canon vera-inc @ {config.canon.commit?.slice(0, 7) ?? 'unknown'} ({config.canon.ref}, synced {config.canon.syncedOn}) · store {config.store}
        </Typography>
      </Stack>
    </Card>
  )
}

// ── 1. concepts ──────────────────────────────────────────────────────────────
const ConceptsCard = ({
  state,
  section,
  slug,
  title,
  latest,
  pickedId,
  onPick,
  writeBlocked,
  notify,
}: {
  state: CoverState
  section: string
  slug: string
  title: string
  latest: CoverConceptSet | null
  pickedId: string | null
  onPick: (c: CoverConcept) => void
  writeBlocked: string | null
  notify: Notify
}) => {
  const gen = useGenerateConcepts(section, slug)
  const [paste, setPaste] = useState('')
  const needsPaste = !state.article.available
  const est = state.estimates.concepts
  const label = `${latest ? 'Regenerate concepts' : 'Generate concepts'} · ${est?.label ?? '~$0.10'}`
  const run = () =>
    gen.mutate(needsPaste ? { articleText: paste, title } : {}, {
      onSuccess: (r) => notify(r.valid ? { type: 'success', text: '4 concepts ready' } : { type: 'warning', text: `Concepts generated with ${r.problems.length} problem(s); see the list` }),
      onError: (e) => notify({ type: 'error', text: (e as Error).message }),
    })
  return (
    <Card title="1 · Concepts" sx={{ p: 4, mb: 0 }}>
      <Stack spacing={3}>
        <Typography variant="body3" color="text.secondary">
          Four ideas, each from one claim the article makes and quoting the sentence it comes from. Rules v{state.config.canon.rulesVersion ?? 1}: real places, people allowed, the object optional; at least three scene types and at most one single-object still life; checked for the house rules (no &quot;chip&quot;, no brand names, no text or tech clichés, no empty title zone: v2.3 uses the whole frame).
        </Typography>
        {needsPaste ? <TextArea label="Article text (the website file could not be read: paste the body)" value={paste} onChange={setPaste} minRows={4} /> : null}
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant={latest ? 'secondary' : 'primary'} disableElevation startIcon={<SparklesIcon strokeWidth={1} size={16} />} disabled={!!writeBlocked || gen.isPending || (needsPaste && paste.trim().length < 200)} onClick={run} title={writeBlocked ?? undefined}>
            {gen.isPending ? 'Generating concepts…' : label}
          </Button>
          {writeBlocked ? <Typography variant="body4" color="text.secondary">{writeBlocked}</Typography> : null}
          {est ? (
            <Typography variant="body4" color="text.secondary">
              estimate ${est.lo.toFixed(3)}–${est.hi.toFixed(3)}, hard ceiling ${est.ceiling?.toFixed(3)}
            </Typography>
          ) : null}
        </Stack>
        {latest ? (
          <>
            {!latest.valid ? <Alert severity="warning" title={`This set breaks ${latest.problems.length} rule(s). Pick with care, or regenerate.`} description={<ul style={{ margin: 0, paddingLeft: 18 }}>{latest.problems.map((p) => <li key={p}>{p}</li>)}</ul>} /> : null}
            {latest.draft.brief ? <Typography variant="body3">{latest.draft.brief}</Typography> : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
              {latest.draft.concepts.map((c) => (
                <ConceptTile key={c.id} c={c} picked={pickedId === c.id} estimate={state.estimates.tests[`${latest.id}:${c.id}`]?.label ?? null} onPick={() => onPick(c)} />
              ))}
            </Box>
            <SampleButton state={state} section={section} slug={slug} set={latest} writeBlocked={writeBlocked} notify={notify} />
            <Typography variant="body4" color="text.secondary">
              Set of {when(latest.createdAt)} · tag mark {latest.draft.tagMark} · object{state.pin?.optional ? ' (optional)' : ''}: {latest.draft.object.slice(0, 160)}
              {latest.draft.object.length > 160 ? '…' : ''}
            </Typography>
          </>
        ) : null}
      </Stack>
    </Card>
  )
}

const ConceptTile = ({ c, picked, estimate, onPick }: { c: CoverConcept; picked: boolean; estimate: string | null; onPick: () => void }) => (
  <Box sx={(t) => ({ border: `1px solid ${picked ? t.palette.primary.main : t.palette.primary[100]}`, borderRadius: '8px', p: 3, backgroundColor: picked ? 'primary.50' : undefined })}>
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {c.id.toUpperCase()} · {c.idea}
        </Typography>
        <Chip size="small" label={SCENE_LABEL[c.scene_type] ?? c.scene_type} />
        <Chip size="small" variant="outlined" label={TAG_LABEL[c.tag_visibility] ?? c.tag_visibility} />
        {c.object_role ? <Chip size="small" variant="outlined" label={ROLE_LABEL[c.object_role] ?? c.object_role} /> : null}
      </Stack>
      <Box component="blockquote" sx={(t) => ({ m: 0, pl: 2, borderLeft: `3px solid ${t.palette.primary[200]}` })}>
        <Typography variant="body3" sx={{ fontStyle: 'italic' }}>
          &ldquo;{c.claim_quote}&rdquo;
        </Typography>
      </Box>
      <Fact k="Subject" v={c.subject_override} />
      <Fact k="Camera" v={c.camera} />
      <Fact k="Scale" v={c.scale} />
      <Fact k="Props" v={(c.props ?? []).join('; ')} />
      <Fact k="Tag" v={c.tag_placement} />
      <Fact k="Violet" v={c.violet_accent} />
      <Fact k="Trade-dress risks" v={(c.trade_dress_risks ?? []).join('; ') || 'none named'} />
      <Fact k="Alt" v={c.alt} />
      <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }}>
        <Button variant={picked ? 'primary' : 'secondary'} size="small" disableElevation onClick={onPick} startIcon={picked ? <CheckIcon size={14} strokeWidth={1.5} /> : undefined}>
          {picked ? 'Picked' : 'Use this concept'}
        </Button>
        {estimate ? <Typography variant="body4" color="text.secondary">4 tests {estimate}</Typography> : null}
      </Stack>
    </Stack>
  </Box>
)

const Fact = ({ k, v }: { k: string; v?: string | null }) =>
  v ? (
    <Typography variant="body4" color="text.secondary">
      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
        {k}:
      </Box>{' '}
      {v}
    </Typography>
  ) : null

/** rules v2: one image of each of the 4 concepts, as written, each with QA (one paid run). */
const SampleButton = ({ state, section, slug, set, writeBlocked, notify }: { state: CoverState; section: string; slug: string; set: CoverConceptSet; writeBlocked: string | null; notify: Notify }) => {
  const gen = useGenerateSample(section, slug)
  const est = state.estimates.sample?.[set.id] ?? null
  const n = set.draft.concepts.length
  return (
    <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
      <Button
        variant="secondary"
        disableElevation
        startIcon={<ImageIcon size={16} strokeWidth={1} />}
        disabled={!!writeBlocked || gen.isPending}
        title={writeBlocked ?? undefined}
        onClick={() =>
          gen.mutate(
            { conceptSetId: set.id },
            {
              onSuccess: () => notify({ type: 'success', text: `${n} images generated, one per concept; QA below` }),
              onError: (e) => notify({ type: 'error', text: (e as Error).message }),
            },
          )
        }
      >
        {gen.isPending ? `Generating ${n} images…` : `1 image of each concept · ${est?.label ?? '~$0.62'}`}
      </Button>
      <Typography variant="body4" color="text.secondary">
        {state.config.models.image} · {state.config.imageSize} · {n} images + {n} QA, each concept as written
        {est ? ` · estimate $${est.lo.toFixed(3)}–$${est.hi.toFixed(3)}` : ''}
      </Typography>
    </Stack>
  )
}

// ── 2. edit ──────────────────────────────────────────────────────────────────
const EditCard = ({ state, section, slug, set, concept, writeBlocked, notify }: { state: CoverState; section: string; slug: string; set: CoverConceptSet; concept: CoverConcept; writeBlocked: string | null; notify: Notify }) => {
  const [metaphor, setMetaphor] = useState(concept.metaphor)
  const [alt, setAlt] = useState(concept.alt)
  const [custom, setCustom] = useState<string | null>(null)
  const gen = useGenerateTests(section, slug)
  const est = state.estimates.tests[`${set.id}:${concept.id}`]
  const edited = metaphor !== concept.metaphor || alt !== concept.alt
  const run = () =>
    gen.mutate(
      { conceptSetId: set.id, conceptId: concept.id, edited: custom === null ? { metaphor, alt } : { alt }, customPrompt: custom },
      {
        onSuccess: () => notify({ type: 'success', text: '4 tests generated; QA below' }),
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )
  return (
    <Card title={`2 · ${concept.id.toUpperCase()} · ${concept.idea}`} sx={{ p: 4, mb: 0 }}>
      <Stack spacing={3}>
        <Typography variant="body3" color="text.secondary">
          The idea paragraph goes into the image prompt as written below; edit it if you want. The house style, the subject and the tag paragraph are added by the generator. Test 1 is as written; tests 2–4 each add one variation (camera, light, framing).
        </Typography>
        {custom === null ? (
          <>
            <TextArea label="THE IDEA (editable)" value={metaphor} onChange={setMetaphor} minRows={5} />
            <Input label="Alt text" value={alt} onChange={setAlt} />
          </>
        ) : (
          <>
            <TextArea label="Custom prompt (the whole image prompt; the house style is no longer added for you)" value={custom} onChange={setCustom} minRows={14} />
            <Input label="Alt text" value={alt} onChange={setAlt} />
          </>
        )}
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
          <Button variant="primary" disableElevation startIcon={<ImageIcon size={16} strokeWidth={1} />} disabled={!!writeBlocked || gen.isPending} onClick={run} title={writeBlocked ?? undefined}>
            {gen.isPending ? 'Generating 4 tests…' : `Generate 4 tests · ${est?.label ?? '~$0.59'}`}
          </Button>
          <Button variant="text" size="small" onClick={() => setCustom(custom === null ? set.prompts[concept.id] ?? metaphor : null)}>
            {custom === null ? 'Write a custom prompt instead' : 'Back to the concept'}
          </Button>
          {edited || custom !== null ? <Chip size="small" label={custom !== null ? 'custom prompt' : 'edited'} /> : null}
          <Typography variant="body4" color="text.secondary">
            {state.config.models.image} · {state.config.imageSize} · 4 images + 4 QA
          </Typography>
        </Stack>
      </Stack>
    </Card>
  )
}

// ── 3. tests ─────────────────────────────────────────────────────────────────
const TestsCard = ({ state, section, slug, title, writeBlocked, notify }: { state: CoverState; section: string; slug: string; title: string; writeBlocked: string | null; notify: Notify }) => {
  const { run, images } = state.tests!
  const [approving, setApproving] = useState<CoverImage | null>(null)
  const [alt, setAlt] = useState('')
  const [upscale, setUpscale] = useState(state.config.mode === 'live')
  const approve = useApproveCover(section, slug)
  const up = state.config.upscale
  const best = useMemo(() => [...images].filter((i) => i.qa && !i.qa.hardFail).sort((a, b) => (b.qa?.score ?? 0) - (a.qa?.score ?? 0))[0]?.id, [images])
  const idea = (images[0]?.concept?.idea as string | undefined) ?? ''
  const sample = (run.input as { sample?: string } | null)?.sample === 'one-per-concept'
  return (
    <Card title={sample ? '3 · One image of each concept' : `3 · Tests: ${idea}`} sx={{ p: 4, mb: 0 }}>
      <Stack spacing={3}>
        <Typography variant="body3" color="text.secondary">
          Run of {when(run.startedAt)} by {run.requestedBy ?? 'unknown'} · {run.mode} · estimated {usd(run.estimatedUsd)} · measured {usd(run.measuredUsd)}. The QA score ranks; it never approves. Pick the one you want: it is the cover as it stands.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {images.map((img) => (
            <TestTile
              key={img.id}
              img={img}
              best={img.id === best}
              approvedId={state.approval?.imageId ?? null}
              disabled={writeBlocked}
              onPick={() => {
                setAlt(img.concept.alt)
                setApproving(img)
              }}
            />
          ))}
        </Box>
      </Stack>
      <ConfirmSettingModal
        open={!!approving}
        onClose={() => setApproving(null)}
        title={`Approve test v${approving?.variant ?? ''} as the cover?`}
        consequence={
          state.config.mode === 'mock'
            ? 'This records the approval in vera-2 and writes the 2400x1340 WebP and the 2K master to the mock store. Mock mode: the website PR is only planned, nothing is sent to GitHub.'
            : `${upscale && up ? `First it upscales this frame ${up.factor} with ${up.model} (~$${up.usd.toFixed(2)}, an unverified estimate; the same picture, larger). Then it ` : 'This '}records the approval, writes the 2400x1340 WebP and the master to ${state.config.store}, and ${
                state.config.apply === 'local'
                  ? `writes the WebP, the image field and the asset-manifest entry into the local website checkout (${state.config.articleSource}), uncommitted. No PR.`
                  : `opens a pull request on ${state.config.website} (branch cover/${slug}) with the file, the image field and the alt text. vera-2 never merges it.`
              }`
        }
        confirmLabel="Approve cover"
        loading={approve.isPending}
        confirmDisabled={!!writeBlocked || !alt.trim()}
        confirmTooltip={writeBlocked ?? undefined}
        onConfirm={() =>
          approving &&
          approve.mutate(
            { imageId: approving.id, alt, title, upscale: upscale && !!up },
            {
              onSuccess: (a) => {
                setApproving(null)
                notify(a.pr?.status === 'failed' ? { type: 'warning', text: `Approved; the PR failed: ${a.pr.error}` } : { type: 'success', text: a.pr?.status === 'opened' ? 'Approved; PR opened' : a.pr?.status === 'written' ? 'Approved; written into the local website checkout' : 'Approved (mock: PR planned)' })
              },
              onError: (e) => notify({ type: 'error', text: (e as Error).message }),
            },
          )
        }
      >
        {approving?.qa?.hardFail ? <Alert severity="warning" title="This test hard-failed QA" description={`${approving.qa.hardFail}. Approve only if you checked it by eye (QA can false-fail).`} /> : null}
        <Input label="Alt text (goes on the article)" value={alt} onChange={setAlt} />
        {up && state.config.mode === 'live' ? (
          <Box component="label" sx={{ display: 'flex', gap: 1, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={upscale} onChange={(e) => setUpscale(e.target.checked)} />
            <Typography variant="body3">
              Upscale {up.factor} first (~${up.usd.toFixed(2)}): the same picture at {up.factor === 'x2' ? '5504x3072' : 'a larger size'}; the WebP is made from it
            </Typography>
          </Box>
        ) : null}
      </ConfirmSettingModal>
    </Card>
  )
}

const TestTile = ({ img, best, approvedId, disabled, onPick }: { img: CoverImage; best: boolean; approvedId: string | null; disabled: string | null; onPick: () => void }) => {
  const { src, isLoading } = useCoverImageUrl(img.stored ? coversApi.imageUrl(img.id) : null)
  const variation = img.concept.variation ? VARIATION_LABEL[img.concept.variation] ?? img.concept.variation : ''
  const c = img.concept as CoverConcept
  return (
    <Box sx={(t) => ({ border: `1px solid ${approvedId === img.id ? t.palette.primary.main : t.palette.primary[100]}`, borderRadius: '8px', overflow: 'hidden' })}>
      <Box sx={{ position: 'relative', aspectRatio: '16 / 9', backgroundColor: '#0e0d12' }}>
        {src ? <img src={src} alt={img.concept.alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : isLoading ? <Skeleton height="100%" /> : null}
        {/* v2.3: what the /insights card keeps (about 1.5:1 at objectPosition 60%, x 0.094-0.938), outlined */}
        {src ? <Box sx={{ position: 'absolute', left: '9.4%', right: '6.2%', top: 0, bottom: 0, border: '1px dashed rgba(220,220,220,0.55)', pointerEvents: 'none' }} title="The /insights card crop" /> : null}
      </Box>
      <Stack spacing={1.5} sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            v{img.variant} · {c.id ? `${String(c.id).toUpperCase()} ${c.idea} · ` : ''}
            {variation}
          </Typography>
          {c.scene_type ? <Chip size="small" label={SCENE_LABEL[c.scene_type] ?? c.scene_type} /> : null}
          {img.qa ? <Chip size="small" color={img.qa.hardFail ? 'error' : best ? 'success' : 'default'} label={img.qa.hardFail ? 'QA hard fail' : `QA ${img.qa.score}${best ? ' · best' : ''}`} /> : null}
          {approvedId === img.id ? <Chip size="small" color="primary" label="Approved" /> : null}
        </Stack>
        {img.error ? <Alert severity="error" title="Not a usable test" description={img.error} /> : null}
        {img.qa?.hardFail ? <Typography variant="body4" color="error.main">{img.qa.hardFail}</Typography> : null}
        {img.qa ? (
          <Stack spacing={0.5}>
            {img.qa.checks.map((ch) => (
              <Stack key={ch.key} direction="row" spacing={1} alignItems="flex-start">
                {ch.ok ? <CheckIcon size={14} strokeWidth={2} color="#2e7d32" /> : <XIcon size={14} strokeWidth={2} color="#c62828" />}
                <Typography variant="body4">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {ch.label}
                  </Box>{' '}
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {ch.detail}
                  </Box>
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
        <Typography variant="body4" color="text.secondary">
          {img.width ? `${img.width}x${img.height}` : 'no image'} · image {usd(img.measuredUsd)} + QA {usd(img.qaUsd, 4)}
          {img.thoughtPreviews ? ` · ${img.thoughtPreviews} thought preview(s) discarded` : ''} · prompt {img.promptSource}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="primary" size="small" disableElevation disabled={!!disabled || !img.stored || !!img.error} onClick={onPick} title={disabled ?? undefined}>
            Pick as cover
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

// ── 4-5. approved ────────────────────────────────────────────────────────────
const ApprovedCard = ({ state, section, slug, title, isAdmin, notify }: { state: CoverState; section: string; slug: string; title: string; isAdmin: boolean; notify: Notify }) => {
  const a = state.approval!
  const { src } = useCoverImageUrl(coversApi.approvedFileUrl(section, slug, 'webp'))
  const retry = useRetryCoverPr(section, slug)
  const pr = a.pr
  return (
    <Card title="Approved cover" sx={{ p: 4, mb: 0 }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4}>
        <Box sx={{ flex: '0 0 auto', width: { xs: '100%', lg: 560 }, aspectRatio: '2400 / 1340', backgroundColor: '#0e0d12', borderRadius: '8px', overflow: 'hidden' }}>
          {src ? <img src={src} alt={a.alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <Skeleton height="100%" />}
        </Box>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Typography variant="body3">
            Approved {when(a.approvedAt)} by {a.approvedBy ?? 'unknown'} ({a.mode}).
          </Typography>
          <Typography variant="body3">
            <b>Alt:</b> {a.alt}
          </Typography>
          <Typography variant="body4" color="text.secondary">
            Files: 2400x1340 WebP ({(a.files.webp.bytes / 1024).toFixed(0)} KB) and the {a.files.master.width}x{a.files.master.height} {(a.files.master as { upscaled?: boolean }).upscaled ? 'upscaled ' : ''}master · on the website: <code>{a.files.website.path}</code> → <code>image.src = &quot;{a.files.website.src}&quot;</code>
          </Typography>
          {pr?.status === 'opened' ? (
            <Alert severity="success" title={`PR #${pr.number} opened on ${state.config.website}`} description={<Link href={pr.url ?? undefined} target="_blank" rel="noopener noreferrer">{pr.url}</Link>} />
          ) : pr?.status === 'written' ? (
            <Alert severity="success" title={`Written into the local website checkout (${pr.branch ?? 'unknown branch'})`} description={`${pr.files.join(', ')}: uncommitted; commit them on that branch. No PR was opened.`} />
          ) : pr?.status === 'mock' ? (
            <Alert severity="info" title="Mock: the website PR was planned, not opened" description={`Would push branch ${pr.branch} with ${pr.files.join(', ')}. Nothing was sent to GitHub.`} />
          ) : pr?.status === 'failed' ? (
            <Alert
              severity="error"
              title="The website PR failed; the approval stands"
              description={pr.error}
              button={
                <Button
                  variant="secondary"
                  size="small"
                  disabled={!isAdmin || retry.isPending}
                  onClick={() => retry.mutate(title, { onSuccess: () => notify({ type: 'success', text: 'PR retried' }), onError: (e) => notify({ type: 'error', text: (e as Error).message }) })}
                >
                  Retry PR
                </Button>
              }
            />
          ) : null}
        </Stack>
      </Stack>
    </Card>
  )
}

// ── run log ──────────────────────────────────────────────────────────────────
const RunLogCard = ({ runs }: { runs: CoverRun[] }) => (
  <Card title="Run log (this article)" sx={{ p: 4, mb: 0 }}>
    {runs.length === 0 ? (
      <Typography variant="body3" color="text.secondary">
        No runs yet.
      </Typography>
    ) : (
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { textAlign: 'left', py: 1, pr: 2, verticalAlign: 'top', borderBottom: '1px solid', borderColor: 'divider' } }}>
        <thead>
          <tr>
            {['When', 'Who', 'Action', 'Mode', 'Model', 'Estimated', 'Measured', 'Outcome'].map((h) => (
              <Typography component="th" variant="body4" key={h} sx={{ fontWeight: 600 }}>
                {h}
              </Typography>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.id}>
              <Typography component="td" variant="body4">{when(r.startedAt)}</Typography>
              <Typography component="td" variant="body4">{r.requestedBy ?? '—'}</Typography>
              <Typography component="td" variant="body4">{r.kind}</Typography>
              <Typography component="td" variant="body4">{r.mode}</Typography>
              <Typography component="td" variant="body4">{r.model}</Typography>
              <Typography component="td" variant="body4">{usd(r.estimatedUsd)}</Typography>
              <Typography component="td" variant="body4">{usd(r.measuredUsd)}</Typography>
              <Typography component="td" variant="body4" color={r.status === 'refused' || r.status === 'failed' ? 'error.main' : undefined}>
                {r.status}
                {r.outcome ? ` · ${r.outcome}` : ''}
                {r.error ? ` · ${r.error}` : ''}
              </Typography>
            </tr>
          ))}
        </tbody>
      </Box>
    )}
  </Card>
)
